import { assertNever } from 'owa-assert';
import {
    AttachmentSelectedFolderIdentifierType,
    AttachmentSelectedFolderInfo,
} from 'owa-attachment-folder-picker';
import {
    OneDriveFolderToUploadToIdentifierType,
    ONE_DRIVE_CONUSMER_API_URL,
    ProgressHandler,
    uploadFileToOneDrive,
    UploadFileToOneDriveParams,
    UploadFileToOneDriveResult,
} from 'owa-fileprovider-services';
import { FileProviderViewState, getFileProviders } from 'owa-fileprovider-store';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';

export function uploadLocalFile(
    fileName: string,
    fileObject: Blob,
    folderInfoToUploadTo: AttachmentSelectedFolderInfo,
    progressHandler: ProgressHandler
): [UploadFileToOneDriveResult, FileProviderViewState] {
    const providerType = isConsumer()
        ? AttachmentDataProviderType.OneDriveConsumer
        : AttachmentDataProviderType.OneDrivePro;
    const fileProviders = getFileProviders();
    const [oneDriveProvider] = fileProviders.filter(fp => fp.type === providerType);

    if (!oneDriveProvider) {
        // Currently we support upsell with slice only for one drive providers
        throw new Error('Unable to upload file as couldnt find OneDrive provider');
    }

    const {
        model: { EndPointUrl: providerEndpointUrl },
    } = oneDriveProvider;

    const uploadParams: UploadFileToOneDriveParams = {
        fileToUpload: { fileName: fileName, fileBlob: fileObject },
        conflictBehavior: 'rename',
        folderInfo: createFolderToUploadToInfo(folderInfoToUploadTo),
        onProgress: progressHandler,
    };

    const uploadResult: UploadFileToOneDriveResult =
        providerType === AttachmentDataProviderType.OneDriveConsumer
            ? uploadFileToOneDrive(
                  AttachmentDataProviderType.OneDriveConsumer,
                  ONE_DRIVE_CONUSMER_API_URL,
                  uploadParams
              )
            : uploadFileToOneDrive(
                  AttachmentDataProviderType.OneDrivePro,
                  providerEndpointUrl,
                  uploadParams
              );

    return [uploadResult, oneDriveProvider];
}

function createFolderToUploadToInfo(
    folderInfo: AttachmentSelectedFolderInfo
): { type: OneDriveFolderToUploadToIdentifierType; identifier: string } {
    const { identifierType, identifier } = folderInfo;
    switch (identifierType) {
        case AttachmentSelectedFolderIdentifierType.Id:
            return { type: OneDriveFolderToUploadToIdentifierType.Id, identifier };
        case AttachmentSelectedFolderIdentifierType.SpecialFolder:
            return { type: OneDriveFolderToUploadToIdentifierType.SpecialFolder, identifier };
        default:
            return assertNever(identifierType);
    }
}
